// src/context/ThemeContext.js - СИСТЕМ ЗА ТЕМА
import React, { createContext, useContext, useState, useEffect } from 'react';
import { createTheme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles';

const ThemeContext = createContext();

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

export const ThemeProvider = ({ children }) => {
    // Земи ја темата од localStorage или користи светла како default
    const [isDarkMode, setIsDarkMode] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme === 'dark';
    });

    // Зачувај ја темата во localStorage кога се менува
    useEffect(() => {
        localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    }, [isDarkMode]);

    const toggleTheme = () => {
        setIsDarkMode(!isDarkMode);
    };

    // Темна тема - Netflix стил
    const darkTheme = createTheme({
        palette: {
            mode: 'dark',
            primary: {
                main: '#ff7eb9',
                light: '#ff4a97',
                dark: '#d6337c',
            },
            secondary: {
                main: '#AB47BC',
                light: '#9C27B0',
                dark: '#7B1FA2',
            },
            background: {
                default: '#0a0a0a', // Netflix black
                paper: '#1a1a1a',
                card: '#2a2a2a'
            },
            text: {
                primary: '#ffffff',
                secondary: '#e5e5e5',
                disabled: '#999999'
            },
            divider: '#333333',
            border: '#444444',
            navbar: '#141414',
            sidebar: '#1a1a1a'
        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h1: { fontWeight: 700 },
            h2: { fontWeight: 700 },
            h3: { fontWeight: 600 },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 500 },
            h6: { fontWeight: 500 },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#141414',
                        borderBottom: '1px solid #333'
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#2a2a2a',
                        border: '1px solid #444'
                    }
                }
            }
        }
    });

    // Светла тема - Модерен стил
    const lightTheme = createTheme({
        palette: {
            mode: 'light',
            primary: {
                main: '#ff7eb9',
                light: '#ff4a97',
                dark: '#d6337c',
            },
            secondary: {
                main: '#AB47BC',
                light: '#9C27B0',
                dark: '#7B1FA2',
            },
            background: {
                default: '#ffffff',
                paper: '#f8f9fa',
                card: '#ffffff'
            },
            text: {
                primary: '#2b2b2b',
                secondary: '#666666',
                disabled: '#999999'
            },
            divider: '#e0e0e0',
            border: '#e0e0e0',
            navbar: '#ffffff',
            sidebar: '#f8f9fa'

        },
        typography: {
            fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
            h1: { fontWeight: 700 },
            h2: { fontWeight: 700 },
            h3: { fontWeight: 600 },
            h4: { fontWeight: 600 },
            h5: { fontWeight: 500 },
            h6: { fontWeight: 500 },
        },
        components: {
            MuiAppBar: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#ffffff',
                        borderBottom: '1px solid #e0e0e0',
                        boxShadow: '0 2px 20px rgba(0,0,0,0.1)'
                    }
                }
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        backgroundColor: '#ffffff',
                        border: '1px solid #e0e0e0',
                        boxShadow: '0 2px 12px rgba(0,0,0,0.1)'
                    }
                }
            }
        }
    });

    const currentTheme = isDarkMode ? darkTheme : lightTheme;

    return (
        <ThemeContext.Provider value={{ isDarkMode, toggleTheme, currentTheme }}>
            <MuiThemeProvider theme={currentTheme}>
                {children}
            </MuiThemeProvider>
        </ThemeContext.Provider>
    );
};